<template>
	<div class="grid-flex-container">
		<div class="grid-toolbar" v-if="showQuickFilter">
			<quick-filter
				:Columns="filterFields"
				:filter="quickFilter"
				@filterChanged="onQuickFilterChanged"
			/>
		</div>
		<div class="generic-grid-container">
			<v-data-table
				:footer-props="{
					itemsPerPageOptions: [10, 15, 20, 25, 30, 50, 100],
					showFirstLastPage: true,
					showCurrentPage: true,
					pagination: {
						page,
						itemsPerPage: rowsPerPage,
						pageStart,
						pageStop,
						pageCount,
						itemsLength: totalCount,
					},
				}"
				:options.sync="options"
				:server-items-length="totalCount"
				hide-default-header
				:hide-default-footer="hideFooter"
				fixed-header
				dense
			>
				<template #header>
					<thead class="v-data-table-header">
						<tr>
							<th
								v-for="(header, index) in columns"
								:key="index"
								@click="sortItems(header.Id)"
								class="active"
							>
								<span v-if="!getSortIndex(header.Id)">{{
									header.Header
								}}</span>
								<span v-else style="color: black">{{
									header.Header
								}}</span>
								<v-icon
									v-if="showAsc(header.Id)"
									dense
									color="black"
									style="font-size: 18px"
									>mdi-arrow-down</v-icon
								>
								<v-icon
									v-if="showDesc(header.Id)"
									dense
									color="black"
									style="font-size: 18px"
									>mdi-arrow-up</v-icon
								>
								<v-badge
									v-if="getSortIndex(header.Id)"
									:content="getSortIndex(header.Id)"
									inline
									color="#e0e0e0"
								></v-badge>
							</th>
						</tr>
						<tr v-if="loading" class="v-data-table__progress">
							<th :colspan="columns.length" class="column">
								<v-progress-linear
									indeterminate
								></v-progress-linear>
							</th>
						</tr>
					</thead>
				</template>
				<template #body>
					<tbody v-if="!loading">
						<tr
							class="v-data-table__empty-wrapper"
							v-if="gridData && gridData.length == 0"
						>
							<td :colspan="columns.length + 1">
								No matching records found
							</td>
						</tr>
						<tr v-for="item in gridData" :key="item.Id">
							<td
								v-for="column in columns"
								:key="column.FieldName"
							>
								{{ item[column.FieldName] }}
							</td>
						</tr>
					</tbody>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ReportPreviewGetters } from '../../../../../store/getters';
import { ReportPreviewActions } from '../../../../../store/actions';
import { FieldDataTypes } from '@/enums/fieldDataTypes';
import { approvalStatus } from '@/enums/approvalStatus';
import gridViewFieldsUtils from '../../../../../utils/gridViewFieldsUtils';
import QuickFilter from '../../../../shared/gridViews/QuickFilter.vue';
export default {
	name: 'ReportPreview',
	components: { QuickFilter },
	props: {
		report: Object,
		showQuickFilter: Boolean,
	},
	computed: {
		...mapGetters({
			columns: [ReportPreviewGetters.REPORT_COLUMNS_CUSTOM],
			rowsPerPage: [ReportPreviewGetters.ROWSPERPAGE],
			totalCount: [ReportPreviewGetters.TOTALCOUNT],
			page: [ReportPreviewGetters.PAGE],
			loading: [ReportPreviewGetters.LOADING],
			gridData: [ReportPreviewGetters.DATA],
			sorting: [ReportPreviewGetters.SORTING],
			quickFilter: [ReportPreviewGetters.QUICKFILTER],
		}),
		filterFields() {
			return this.columns.map((c) => ({ Id: c.Id, Label: c.Header }));
		},
		sortIndex() {
			if (!this.sorting) return [];
			return this.sorting.map((x, index) => ({
				FieldId: x.FieldId,
				Index: index + 1,
			}));
		},
		FieldDataTypes() {
			return FieldDataTypes;
		},
		ApprovalStatus() {
			return approvalStatus;
		},
		hideFooter() {
			if (this.loading) return true;
			if (this.gridData && this.gridData.length <= 0) return true;
			return false;
		},
		pageStart() {
			return this.rowsPerPage * (this.page - 1);
		},
		pageStop() {
			return this.rowsPerPage * this.page;
		},
		pageCount() {
			if (!this.rowsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(this.totalCount / this.rowsPerPage);
			let remainder = this.totalCount % this.rowsPerPage;
			if (remainder === 0) return quotient;
			return quotient + 1;
		},
		options: {
			get() {
				return {
					page: this.page,
					itemsPerPage: this.rowsPerPage,
				};
			},
			set(data) {
				if (this.page != data.page && !isNaN(data.page))
					this.$store.dispatch(
						ReportPreviewActions.UPDATE_PAGE,
						data.page
					);
				if (
					this.rowsPerPage != data.itemsPerPage &&
					!isNaN(data.itemsPerPage)
				)
					this.$store.dispatch(
						ReportPreviewActions.UPDATE_ROWSPERPAGE,
						data.itemsPerPage
					);
			},
		},
		selectedPage() {
			return this.page;
		},
		selectedRowsPerPage() {
			return this.rowsPerPage;
		},
	},
	mounted() {
		if (this.report) {
			this.$store
				.dispatch(ReportPreviewActions.LOAD_REPORT, {
					report: this.report,
					workbook: null,
				})
				.then(() => {
					this.$store.dispatch(ReportPreviewActions.LOAD_DATA_CUSTOM);
				});
		}
	},
	destroyed() {
		this.$store.dispatch(ReportPreviewActions.CLEAR);
	},
	methods: {
		getFieldName(field) {
			return gridViewFieldsUtils.getFieldName(field);
		},
		showDesc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) => column.FieldId === headerId && column.Operator === 1
			);
		},
		showAsc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) =>
					column.FieldId === headerId && column.Operator === -1
			);
		},
		sortItems(headerId) {
			let localSorting = this.sorting
				? JSON.parse(JSON.stringify(this.sorting))
				: [];
			let sorterIndex = localSorting.findIndex(
				(column) => column.FieldId === headerId
			);
			if (
				sorterIndex !== -1 &&
				localSorting[sorterIndex].Operator === -1
			) {
				localSorting.splice(sorterIndex, 1);
			} else if (
				sorterIndex !== -1 &&
				localSorting[sorterIndex].Operator === 1
			) {
				localSorting[sorterIndex].Operator = -1;
			} else {
				localSorting.push({ Operator: 1, FieldId: headerId });
			}
			this.$store.dispatch(
				ReportPreviewActions.UPDATE_SORTING,
				localSorting
			);
		},
		getSortIndex(fieldId) {
			let result = null;
			this.sortIndex.forEach((x) => {
				if (x.FieldId == fieldId) result = x.Index;
			});
			return result;
		},
		onQuickFilterChanged(item) {
			this.$store.dispatch(
				ReportPreviewActions.UPDATE_QUICK_FILTER,
				item
			);
		},
		onClose() {
			this.$emit('close');
		},
	},
};
</script>
